/**
 * The map bar's players filter (maintainer, 2026-11-03): a chip per player in
 * free-for-all, or per team in team games. The selected chips' territories
 * are brought forward on the map and the rest darkened (the same scrim the
 * rules overlay uses). Pure: no React. The selection itself lives in the view
 * group state (lib/mapViewGroup), so closing the bar clears it.
 *
 * Only land the viewer can see as owned counts: fogged territories whose
 * owner is hidden (`ownerHidden`) are skipped. Eliminated players, and teams
 * whose every member is out, get no chip.
 */
import { teamLabel, teamMembers, type PlayerId, type TeamId, type TeamMembership } from '@warcarp/api-types'

export interface PlayerFilterOption {
  /** Chip key: the player id in free-for-all, `team:A` in team games. */
  key: string
  label: string
  /** The player's colour, or each team member's in turn order. */
  colors: string[]
  playerIds: PlayerId[]
}

export interface PlayerFilterInput {
  /** Turn order. */
  players: readonly PlayerId[]
  teams?: TeamMembership
  eliminated?: readonly PlayerId[]
  colorOf: (playerId: PlayerId) => string
  nameOf: (playerId: PlayerId) => string
}

export function playerFilterOptions({ players, teams, eliminated = [], colorOf, nameOf }: PlayerFilterInput): PlayerFilterOption[] {
  const out = new Set(eliminated)
  if (teams && Object.keys(teams).length > 0) {
    const state = { teams, players }
    const order: TeamId[] = []
    for (const p of players) {
      const team = teams[p]
      if (team && !order.includes(team)) order.push(team)
    }
    return order.flatMap((team) => {
      const members = teamMembers(state, team)
      if (members.every((p) => out.has(p))) return []
      return [{ key: `team:${team}`, label: teamLabel(team), colors: members.map(colorOf), playerIds: members }]
    })
  }
  return players.filter((p) => !out.has(p)).map((p) => ({ key: p, label: nameOf(p), colors: [colorOf(p)], playerIds: [p] }))
}

/**
 * The territories the selection brings forward: the union of the selected
 * chips' visible land. null when nothing is selected (no scrim); an empty
 * list when the selection holds no land the viewer can see (all dimmed).
 */
export function filteredTerritoryIds(
  territories: ReadonlyArray<{ id: string; ownerId?: PlayerId | null; ownerHidden?: boolean }>,
  options: readonly PlayerFilterOption[],
  selected: readonly string[],
): string[] | null {
  const owners = new Set(options.filter((o) => selected.includes(o.key)).flatMap((o) => o.playerIds))
  if (owners.size === 0) return null
  return territories.filter((t) => !!t.ownerId && !t.ownerHidden && owners.has(t.ownerId)).map((t) => t.id)
}

/**
 * Chip indices split into the fewest lines that fit `width`, as evenly as the
 * count allows (each line gets ⌈n / lines⌉ or one fewer, the longer lines
 * first), in order. Widths include each chip's margin.
 */
export function balancedRows(widths: readonly number[], width: number): number[][] {
  const n = widths.length
  for (let lines = 1; lines <= n; lines++) {
    const rows: number[][] = []
    let start = 0
    for (let r = 0; r < lines; r++) {
      const size = Math.ceil((n - start) / (lines - r))
      rows.push(Array.from({ length: size }, (_, k) => start + k))
      start += size
    }
    if (rows.every((row) => row.reduce((sum, i) => sum + widths[i]!, 0) <= width)) return rows
  }
  return widths.map((_, i) => [i])
}
